import type { DateRange, SojournDetailDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { formatRange } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Badge, Button, CollapsibleCard, ConfirmButton, Select } from '../../shared/ui/index.js';
import { useGuests } from '../guests/guestHooks.js';
import { DateRangeEditor } from './DateRangeEditor.js';
import { useRemoveSojournGuest, useSetSojournGuest } from './sojournHooks.js';

export function SojournGuests({ sojourn }: { sojourn: SojournDetailDto }): ReactElement {
  const t = useTranslation();
  const guests = useGuests();
  const setGuest = useSetSojournGuest(sojourn.id);
  const removeGuest = useRemoveSojournGuest(sojourn.id);
  const [editing, setEditing] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState('');

  const attending = new Set(sojourn.guests.map((guest) => guest.guestId));
  const notAttending = (guests.data ?? []).filter((guest) => !attending.has(guest.id));
  const availableTags = [...new Set(notAttending.flatMap((guest) => guest.tags))].sort();
  const available =
    tagFilter === ''
      ? notAttending
      : notAttending.filter((guest) => guest.tags.includes(tagFilter));
  const bounds: DateRange = { start: sojourn.startDate, end: sojourn.endDate };

  return (
    <CollapsibleCard
      title={t.sojournGuests.title}
      subtitle={t.count.guests(sojourn.guests.length)}
      error={setGuest.error ?? removeGuest.error}
      actions={
        <>
          {availableTags.length > 0 ? (
            <Select
              aria-label={t.sojournGuests.filterByTag}
              value={tagFilter}
              onChange={(event) => setTagFilter(event.target.value)}
            >
              <option value="">{t.sojournGuests.anyTag}</option>
              {availableTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </Select>
          ) : null}
          {notAttending.length > 0 ? (
            <>
              <Select
                aria-label={t.sojournGuests.addGuest}
                value=""
                onChange={(event) => {
                  if (event.target.value !== '') {
                    setGuest.mutate({ guestId: event.target.value });
                  }
                }}
              >
                <option value="">{t.sojournGuests.addGuestPlaceholder}</option>
                {available.map((guest) => (
                  <option key={guest.id} value={guest.id}>
                    {guest.name}
                  </option>
                ))}
              </Select>
              <Button
                small
                variant="secondary"
                disabled={available.length === 0}
                loading={setGuest.isPending}
                onClick={() => {
                  for (const guest of available) {
                    setGuest.mutate({ guestId: guest.id });
                  }
                }}
              >
                {t.sojournGuests.addEveryone(tagFilter === '' ? null : tagFilter)}
              </Button>
            </>
          ) : null}
        </>
      }
    >
      {sojourn.guests.length === 0 ? (
        <p className="field__hint">{t.sojournGuests.emptyDescription}</p>
      ) : (
        <ul className="data-list">
          {sojourn.guests.map((guest) => (
            <li key={guest.guestId} className="list-row" style={{ alignItems: 'flex-start' }}>
              <div className="list-row__main" style={{ flex: '1 1 320px' }}>
                <span className="list-row__title">{guest.guestName}</span>

                {editing === guest.guestId ? (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <DateRangeEditor
                      ranges={guest.stays}
                      bounds={bounds}
                      disabled={setGuest.isPending}
                      onChange={(stays) => setGuest.mutate({ guestId: guest.guestId, stays })}
                    />
                  </div>
                ) : (
                  <div className="stack stack--tight" style={{ marginTop: 'var(--space-1)' }}>
                    <div className="option-list">
                      {guest.stays.slice(0, 2).map((range) => (
                        <Badge key={`${range.start}-${range.end}`}>{formatRange(range)}</Badge>
                      ))}
                      {guest.stays.length > 2 ? <Badge>+{guest.stays.length - 2}</Badge> : null}
                    </div>
                    <span className="list-row__meta">{t.count.nights(guest.nights)}</span>
                  </div>
                )}
              </div>

              <div className="row">
                <Button
                  small
                  variant="secondary"
                  onClick={() => setEditing(editing === guest.guestId ? null : guest.guestId)}
                >
                  {editing === guest.guestId ? t.common.done : t.sojournGuests.dates}
                </Button>
                <ConfirmButton
                  small
                  label={t.common.remove}
                  onConfirm={() => removeGuest.mutate(guest.guestId)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}
